"use client";

import { useQuery } from "@tanstack/react-query";
import React, { useMemo } from "react";
import { Input, Label } from "@/components/ui";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { calculateWeightInsightsAtDate } from "@/lib/weight-calculations";
import { Number as NumberQuestion, QuestionData } from "@/types/forms/question";
import { NoteAnswerDetail, NoteQuestionDetail } from "@/types/notes";
import { WeightInsights } from "./weight-insights";
import { noteQueryOptions } from "@/api/provider/scribe/note-query";
import { patientQueryOptions } from "@/api/provider/user/patient/patient-query";
interface WeightAnswerEditorProps {
  answer: NoteAnswerDetail;
  question: NoteQuestionDetail;
  onChange: (answerId: string, data: Record<string, any>, noteAttachmentId?: string) => void;
  disabled?: boolean;
  noteId?: string;
  patientId?: string;
}
export const WeightAnswerEditor: React.FC<WeightAnswerEditorProps> = ({
  answer,
  question,
  onChange,
  disabled = false,
  noteId,
  patientId
}) => {
  const questionData = question.data as QuestionData;

  // Fetch patient data to get measurements
  const {
    data: patient,
    isLoading: isLoadingPatient
  } = useQuery({
    ...patientQueryOptions.detail(patientId || ""),
    enabled: !!patientId
  });

  // Fetch note detail to get createdDate for reference
  const {
    data: noteDetail,
    isLoading: isLoadingNote
  } = useQuery({
    ...noteQueryOptions.detail(noteId || ""),
    enabled: !!noteId
  });

  // Handle both old format {pounds: number} and new format {value: number}
  const currentValue = answer.data.value ?? ("pounds" in answer.data ? answer.data.pounds : null) ?? "";

  // Days are compared on the clinic clock, so insights follow its zone.
  const clock = useClinicClock();
  const weightInsights = useMemo(() => {
    if (!patient?.measurements || !currentValue || currentValue === "") {
      return null;
    }

    // Use note's createdDate as reference, or current date for new notes
    const referenceDate = noteDetail?.createdDate ? new Date(noteDetail.createdDate) : new Date();
    return calculateWeightInsightsAtDate(patient.measurements, Number(currentValue), referenceDate, clock);
  }, [patient?.measurements, currentValue, noteDetail?.createdDate, clock]);
  const isLoadingInsights = isLoadingPatient || isLoadingNote;
  const showInsights = !!patientId && currentValue !== "";
  return <>
      <Label className="text-body-strong">{questionData.name}</Label>
      <div className="flex items-center gap-3">
        <div className="relative w-28 shrink-0">
          <Input type="number" value={currentValue} onChange={e => {
          const value = e.target.value ? parseFloat(e.target.value) : null;
          onChange(answer.id, {
            value
          });
        }} placeholder={(questionData as NumberQuestion).placeholder} disabled={disabled} className="pr-8" />
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-body text-foreground-tertiary">
            lbs
          </span>
        </div>
        {showInsights && <WeightInsights changeSinceLastVisit={weightInsights?.changeSinceLastVisit ?? null} totalWeightChange={weightInsights?.totalWeightChange ?? null} previousDate={weightInsights?.previousMeasurementDate ?? null} firstDate={weightInsights?.firstMeasurementDate ?? null} isLoading={isLoadingInsights} />}
      </div>
    </>;
};